import capitalize from 'lodash/capitalize';
import isEmpty from 'lodash/isEmpty';
import last from 'lodash/last';
import isNil from 'lodash/isNil';
import compact from 'lodash/compact';
import uniq from 'lodash/uniq';
import map from 'lodash/map';
import omit from 'lodash/omit';
import uniqueId from 'lodash/uniqueId';
import {
  formatPhone,
  isEmptyString,
  notEmptyString,
  setDefaultValueIf,
} from '@populateui/utils';
import {
  formatDate,
  formatDateOnly,
  formatDateTime,
  formatDateTimeOffset,
  formatISODateTime,
  getDateRangeString,
  getTimeRangeString,
} from 'utils/date';
import dayjs, {
  getDefaultTimeZoneId,
} from 'utils/dayjs';
import {
  DateRange,
} from 'types/dateRange';
import {
  InsuranceFormInfo,
} from 'pages/Dashboard/pages/Appointments/pages/List/types/patient';
import {
  PatientCompact,
} from 'pages/Dashboard/pages/Encounters/types/patient';
import {
  AppointmentDTO,
  InsuranceDTO,
  BlockedTimeSlotDTO,
  ConfirmAppointmentCommuniqueDTO,
  PatientDTO,
  MultiMediaResponse,
  CalendarEventsResponseDTO,
  ProviderAvailabilityDTO,
  WeeklyRecurringAvailabilityDTO,
  DailyRecurringAvailabilityDTO,
  SingleAvailabilityDTO,
  PagePropertiesRequestDTO,
  AppointmentDetailsDTO,
  SingleBlockedTimeSlotDTO,
  DailyRecurringBlockedTimeSlotDTO,
  WeeklyRecurringBlockedTimeSlotDTO,
  UserDTO,
  InsuranceRelationshipTypeSettingDTO,
  PatientSummaryDTO,
  FormattedNamesDTO,
} from 'dtos';
import {
  RecurrenceTypes,
} from 'dtos/recurrenceTypes';
import {
  CalendarEvent,
} from 'pages/Dashboard/pages/Appointments/pages/List/types/appointment';
import {
  AppointmentIntakeSectionStatus,
  AppointmentIntakeStatus,
} from 'pages/Dashboard/types/appointmentIntakeStatus';
import {
  getMultiMedia,
} from 'pages/Dashboard/services/api';
import {
  MultimediaType,
  MediaOriginatorType,
} from 'pages/Dashboard/utils/constants';
import {
  weekDays,
  weekDaysValuesList,
} from 'core/constants';
import {
  MbscRecurrenceRule,
} from '@mobiscroll/react';
import {
  AppointmentForm,
} from 'pages/Dashboard/pages/Appointments/pages/List/components/Details';
import {
  FileResultDTO,
} from 'dtos/fileResultDTO';
import {
  ModifyAppointmentDetailsRequestDTO,
} from 'dtos/modifyAppointmentDetailsRequestDTO';
import sortBy from 'lodash/sortBy';
import type {
  InsuranceDataForm,
} from 'views/EMR/components/Insurance/Dialog/InsuranceForm';
import {
  REQUIRED_ADDRESS_FIELDS,
} from 'views/EMR/components/Insurance/utils';
import values from 'lodash/values';
import pick from 'lodash/pick';
import {
  getPatientAddress,
} from 'pages/Dashboard/utils/patient';
import {
  displayNameKey,
  initialsKey,
} from 'types/nameFormat';

const EMAIL_REGEX = /^(?:[A-Za-z0-9](?:[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]*[A-Za-z0-9])?)*|"[^"]+")@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;
// Valid:
// John
// José
// Muñoz
// François
// Müller
// محمد
// 李
// Иван
// Mary Ann
// Jean-Luc
// O'Brien
// O’Brien
// John2
// 3M
// 123

// Invalid:
// empty
// John,
// John@Doe
// -Smith
// Mary  Ann
// O''Brien
// O‘Brien
export const PATIENT_NAME_REGEX = /^[\p{L}\p{M}\p{N}]+(?:[ '\u2019-][\p{L}\p{M}\p{N}]+)*$/u;

export function normalizePatientName(value?: string | null): string {
  return value?.trim() ?? '';
}

export function validatePatientName(
  value: string | null | undefined,
  message: string,
  required: boolean = true,
): true | string {
  const trimmed = normalizePatientName(value);
  if (isEmptyString(trimmed)) {
    return required ? message : true;
  }
  return PATIENT_NAME_REGEX.test(trimmed) ? true : message;
}

export function normalizePhoneNumber(phone: string) {
  const phoneNumber = phone?.replace(/\D/g, '');
  return notEmptyString(phoneNumber) && phoneNumber?.length === 11
    ? `+${phoneNumber}` : undefined;
}

export function normalizeNumber(number: string) {
  return number?.replace(/\D/g, '');
}

export const validateEmail = (email?: unknown): boolean => EMAIL_REGEX.test(typeof email === 'string' ? email : String(email ?? ''));
export const validatePhone = (phone?: string | null): boolean => {
  const phoneNumber = normalizePhoneNumber(phone ?? '');
  return /^\+\d{11}$/i.test(phoneNumber ?? '');
};

export const validateSSN = (
  ssn?: string | null,
): boolean => isEmptyString(ssn) || ssn?.length === 9;

export const validateEmailIfSet = (email: unknown = '') => isEmptyString(typeof email === 'string' ? email : String(email ?? '')) || validateEmail(email);
export const validatePhoneIfSet = (phone: unknown = '') => {
  const phoneNumber = normalizeNumber(typeof phone === 'string' ? phone : String(phone ?? '')) ?? '';
  return phoneNumber?.substring(1).length === 0 || validatePhone(phoneNumber);
};
export const validateDateRange = (dateRange: unknown) => {
  const { min, max } = (dateRange ?? {}) as Partial<DateRange>;
  return !isNil(min) && !isNil(max) && dayjs(min).isBefore(dayjs(max));
};

export function validatePatient(patient: unknown) {
  return isNil((patient as PatientDTO | null | undefined)?.patientId) ? 'Select patient' : true;
}

type MergedProviderAvailabilityType = ProviderAvailabilityDTO & SingleAvailabilityDTO &
  DailyRecurringAvailabilityDTO & WeeklyRecurringAvailabilityDTO;

type MergedTimeBlockType = SingleBlockedTimeSlotDTO
  & DailyRecurringBlockedTimeSlotDTO
  & WeeklyRecurringBlockedTimeSlotDTO;

type MergedRecurrenceType = MergedTimeBlockType & MergedProviderAvailabilityType;

function normalizeEventDateAttributes(startDate: string, durationInMinutes: number = 0) {
  const timezoneId = getDefaultTimeZoneId();
  const start = dayjs.tz(startDate, timezoneId);
  const end = start.add(durationInMinutes, 'minutes');
  const eventDateRange = [start, end];

  return {
    start,
    end,
    eventDateRange,
    formattedStartDateTime: formatDateTime(start),
    formattedDateRange: getDateRangeString(start, end),
    formattedTimeRange: getTimeRangeString(start, end),
    durationInMinutes,
  };
}

function normalizePatientAttributes(patient: PatientDTO) {
  return {
    patient,
    formattedPatientPhoneNumber: formatPhone(patient?.phoneNumber?.phoneNumberWithAreaCode ?? ''),
    lastVisitDateTime: patient?.visitNotes?.length
      ? formatDateTime(last(patient?.visitNotes)?.visitDateTime ?? '') : null,
  };
}

function getIntakeItemStatus(
  isRequired: boolean,
  isSubmitted: boolean,
): AppointmentIntakeSectionStatus {
  let status: AppointmentIntakeSectionStatus = 'NotSent';
  if (isRequired) {
    status = isSubmitted ? 'Completed' : 'Incomplete';
  }
  return status;
}

export function getAppointmentIntakeSummary(
  intakeStatus: AppointmentIntakeStatus,
) {
  const sectionStatuses = [
    intakeStatus.hpi,
    intakeStatus.medicalHistory,
    intakeStatus.pharmacyAllergyMedications,
  ];
  const requestedCount = sectionStatuses.filter((status) => status !== 'NotSent').length;
  const completedCount = sectionStatuses.filter((status) => status === 'Completed').length;

  let summaryStatus = intakeStatus.wasCommuniqueSent ? 'Reminder only' : 'Not sent';
  if (requestedCount > 0 && completedCount === requestedCount) {
    summaryStatus = 'Completed';
  } else if (requestedCount > 0 && completedCount === 0) {
    summaryStatus = 'Incomplete';
  } else if (requestedCount > 0) {
    summaryStatus = 'Partially completed';
  }

  return {
    requestedCount,
    completedCount,
    summaryStatus,
  };
}

export function getFallbackAppointmentIntakeStatus(
  confirmAppointmentCommunique: ConfirmAppointmentCommuniqueDTO | null = null,
): AppointmentIntakeStatus {
  const isHPIRequired = confirmAppointmentCommunique?.sendForms?.hpi === true;
  const isMedicalHistoryRequired = (
    confirmAppointmentCommunique?.sendForms?.medicalHistory === true
  );
  const hpi = getIntakeItemStatus(
    isHPIRequired,
    confirmAppointmentCommunique?.intakeSubmissionStatus?.hpiSubmitted === true,
  );
  const medicalHistory = getIntakeItemStatus(
    isMedicalHistoryRequired,
    confirmAppointmentCommunique?.intakeSubmissionStatus?.medicalHistorySubmitted === true,
  );
  const pharmacyAllergyMedications = getIntakeItemStatus(
    isMedicalHistoryRequired,
    confirmAppointmentCommunique
      ?.intakeSubmissionStatus?.pharmacyAllergyMedicationsSubmitted === true,
  );

  return {
    isComplete: !isNil(confirmAppointmentCommunique)
      && ![hpi, medicalHistory, pharmacyAllergyMedications].includes('Incomplete'),
    wasCommuniqueSent: !isNil(confirmAppointmentCommunique),
    hpi,
    medicalHistory,
    pharmacyAllergyMedications,
  };
}

export function normalizeIntakeFormStatus(
  confirmAppointmentCommunique: ConfirmAppointmentCommuniqueDTO | null = null,
): boolean {
  return getFallbackAppointmentIntakeStatus(confirmAppointmentCommunique).isComplete;
}

export function normalizeInsurance(insurance: InsuranceFormInfo): InsuranceDTO {
  const insuranceInfo = omit(insurance, ['insuranceOwnerDOB', 'effectiveFrom', 'effectiveTill']);
  return {
    ...(insuranceInfo ?? {}),
    ...setDefaultValueIf(
      insuranceInfo ?? {},
      null,
      isEmptyString,
    ),
    mspType: insurance?.mspType?.code,
    inactive: insurance?.inactive ?? false,
    insuranceSlot: insurance?.insuranceSlot,
    insuranceOwnerDOB: formatDateOnly(insurance?.insuranceOwnerDOB ?? dayjs()),
    insuranceOwnerPhoneNumber: normalizePhoneNumber(insurance?.insuranceOwnerPhoneNumber ?? ''),
    effectiveFrom: formatDateOnly(insurance?.effectiveFrom ?? dayjs()),
    effectiveTill: isNil(insurance?.effectiveTill)
      ? null : formatDateOnly(insurance?.effectiveTill ?? dayjs()),
  };
}

export function serializeInsurance(
  insurance: InsuranceDataForm,
  patient: PatientSummaryDTO,
  insuranceOwnerRelationshipType: InsuranceRelationshipTypeSettingDTO,
) {
  const { isSubscriberPatient, isEmployerInsurance } = insuranceOwnerRelationshipType;
  const isSelf = isSubscriberPatient || isEmployerInsurance;
  const insuranceInfo = omit(insurance, ['insuranceOwnerDOB', 'effectiveFrom', 'effectiveTill', 'demographics']);
  const { demographics } = insurance ?? {};
  const hasPhone = notEmptyString(patient?.phoneNumber?.phoneNumberWithAreaCode);
  const insuranceOwnerPhoneNumber = isSelf && hasPhone
    ? patient?.phoneNumber?.phoneNumberWithAreaCode
    : insurance.insuranceOwnerPhoneNumber;

  const hasInputAddress = values(
    pick(insurance.insuranceOwnerAddress, REQUIRED_ADDRESS_FIELDS),
  ).some((item) => !isEmptyString(item));

  const inputAddress = hasInputAddress
    ? insurance.insuranceOwnerAddress
    : undefined;

  const hasAddress = !isNil(patient?.address) && values(
    pick(patient?.address, REQUIRED_ADDRESS_FIELDS),
  ).every((item) => !isEmptyString(item));

  const insuranceOwnerAddress = (
    isSelf && hasAddress
      ? getPatientAddress(patient?.address ?? {})
      : inputAddress
  );

  const insuranceOwnerAddressId = (
    isSelf && hasAddress
      ? patient?.address?.addressId
      : insurance?.insuranceOwnerAddressId
  ) ?? 0;

  return {
    ...(insuranceInfo ?? {}),
    ...setDefaultValueIf(
      insuranceInfo ?? {},
      null,
      isEmptyString,
    ),
    mspType: insurance?.mspType?.code,
    inactive: insurance?.inactive ?? false,
    insuranceSlot: insurance?.insuranceSlot,
    insuranceOwnerDOB: formatDateOnly(demographics?.dateOfBirth ?? dayjs()),
    insuranceOwnerPhoneNumber: normalizePhoneNumber(insuranceOwnerPhoneNumber ?? ''),
    insuranceOwnerFirstName: demographics?.firstName,
    insuranceOwnerMiddleName: demographics?.middleName,
    insuranceOwnerLastName: demographics?.lastName,
    insuranceOwnerGenderDescription: demographics?.genderDescription,
    effectiveFrom: formatDateOnly(insurance?.effectiveFrom ?? dayjs()),
    effectiveTill: isNil(insurance?.effectiveTill)
      ? null : formatDateOnly(insurance?.effectiveTill ?? dayjs()),
    insuranceOwnerAddressId: isNil(insuranceOwnerAddress)
      ? null
      : insuranceOwnerAddressId,
    insuranceOwnerAddress: insuranceOwnerAddress ?? null,
  } as InsuranceDTO;
}

export function normalizeAppointment({
  encounterId,
  insuranceId,
  userId,
  patient,
  patientAppointmentStatus,
  startDateTime,
  durationInMinutes,
  typeOfVisitDescription,
  reasonForVisit,
  canBeRescheduledSooner,
  confirmAppointmentCommunique,
  copayCollected,
  serviceFacility,
  referringProvider,
  tag,
  isOutOfPocket,
  isShiftedToPatient,
  createdByUser,
  createdOn,
  patientAlerts,
}: AppointmentDTO): CalendarEvent {
  return {
    id: `visit-${encounterId}`,
    objectId: encounterId!,
    encounterId,
    insuranceId,
    userId: userId!,
    typeName: 'appointment',
    isTimeBlock: false,
    isTimeAvailability: false,
    typeOfVisitDescription: typeOfVisitDescription ?? '',
    patientAppointmentStatus: patientAppointmentStatus ?? '',
    reason: reasonForVisit ?? '',
    canBeRescheduledSooner,
    ...normalizeEventDateAttributes(startDateTime!, durationInMinutes),
    ...normalizePatientAttributes(patient!),
    createdByUser,
    confirmAppointmentCommunique,
    intakeFormCompleted: normalizeIntakeFormStatus(confirmAppointmentCommunique),
    consentStatus: patient?.consentStatus,
    copayCollected,
    serviceFacility,
    referringProvider,
    isOutOfPocket,
    isShiftedToPatient,
    tag,
    createdOn,
    startDateTime,
    patientAlerts,
  };
}

export function normalizeAppointmentWithPayments({
  appointment,
  currentAppointmentDue,
  hasPaymentAllocation,
  copayPaid,
  copayTotal,
  totalDue,
  credit,
}: AppointmentDetailsDTO) {
  const normalizedAppointment = normalizeAppointment(appointment ?? {});
  return {
    ...normalizedAppointment,
    currentAppointmentDue,
    copayPaid,
    copayTotal,
    hasPaymentAllocation,
    totalDue,
    credit,
  };
}

function normalizeRecurrence({
  recurrenceType,
  interval,
  repeatsOn,
  count,
  endsOn,
}: SingleAvailabilityDTO & DailyRecurringAvailabilityDTO & WeeklyRecurringAvailabilityDTO):
  MbscRecurrenceRule {
  return {
    ...(recurrenceType && recurrenceType !== RecurrenceTypes.Single
      ? { repeat: recurrenceType?.toLowerCase() as MbscRecurrenceRule['repeat'] } : {}),
    ...(!isNil(interval) ? { interval } : {}),
    ...(!isNil(count) ? { count } : {}),
    ...(!isNil(endsOn) ? { endsOn: formatDateTimeOffset(endsOn) } : {}),
    ...(!isNil(repeatsOn) ? {
      weekDays: repeatsOn?.map((day) => weekDays[day]).join(','),
      repeatsOn: repeatsOn?.map((day) => weekDaysValuesList.indexOf(day)),
    } : {}),
  };
}

export function normalizeRecurringEvents({
  recurrenceType,
  interval,
  repeatsOn,
  count,
  endsOn,
  userId,
  startDateTime,
  durationInMinutes,
}: MergedRecurrenceType): Omit<CalendarEvent, 'id' | 'objectId'> {
  const recurrence = normalizeRecurrence({
    recurrenceType, endsOn, interval, repeatsOn, count,
  });

  return {
    userId: userId!,
    ...(recurrenceType === RecurrenceTypes.Single ? {} : { recurrence }),
    ...normalizeEventDateAttributes(startDateTime!, durationInMinutes),
  };
}

export function normalizeTimeBlock({
  blockedTimeSlotId,
  reasonForBlock,
  ...data
}: MergedTimeBlockType): CalendarEvent {
  return {
    ...normalizeRecurringEvents(data),
    id: uniqueId(`blockedTime-${blockedTimeSlotId}__`),
    objectId: blockedTimeSlotId!,
    isTimeBlock: true,
    isTimeAvailability: false,
    typeName: 'timeBlock',
    typeOfVisitDescription: 'Time Blocked',
    reason: reasonForBlock ?? '',
  };
}

export function normalizeAvailability({
  providerAvailabilityId,
  serviceFacilityId,
  serviceFacility,
  isDefault,
  ...data
}: MergedProviderAvailabilityType): CalendarEvent {
  return {
    ...normalizeRecurringEvents(data),
    id: uniqueId(`availability-${providerAvailabilityId}__`),
    objectId: providerAvailabilityId!,
    typeName: 'availability',
    typeOfVisitDescription: 'Availability',
    serviceFacilityId,
    serviceFacility,
    isTimeAvailability: true,
    isTimeBlock: false,
    isDefault,
  };
}

export function normalizeEvents(eventsDictionary: CalendarEventsResponseDTO): CalendarEvent[] {
  const { appointments, blockedTimeSlots, availabilities } = eventsDictionary;

  const normalizedAppointments = appointments?.map(normalizeAppointment);
  const normalizedBlockedTimeSlots = blockedTimeSlots?.map(normalizeTimeBlock);
  const normalizedAvailabilities = availabilities?.map(normalizeAvailability);

  return (normalizedAppointments ?? [])
    .concat((normalizedBlockedTimeSlots ?? []))
    .concat((normalizedAvailabilities ?? []));
}

export function serializeAppointmentForUpdate({
  objectId,
  start,
  durationInMinutes,
  typeOfVisitDescription,
  userId,
  reason: reasonForVisit,
  serviceFacility,
  referringProvider,
  canBeRescheduledSooner,
  tag,
}: Partial<CalendarEvent>): ModifyAppointmentDetailsRequestDTO {
  return {
    encounterId: objectId,
    startDateTime: !isNil(start) ? formatISODateTime(start) : undefined,
    reasonForVisit,
    durationInMinutes,
    typeOfVisitDescription,
    userId,
    serviceFacilityId: serviceFacility?.serviceFacilityId,
    referringProvider,
    canBeRescheduledSooner,
    tag,
  };
}

export function serializeTimeBlock({
  objectId,
  start,
  durationInMinutes,
  userId,
  reason: reasonForBlock,
  recurrence,
}: Partial<CalendarEvent>): BlockedTimeSlotDTO {
  const {
    repeatType,
    interval,
    repeatsOn,
    endsOn,
    count,
  } = recurrence ?? {};
  return {
    recurrenceType: (isNil(repeatType)
      ? RecurrenceTypes.Single
      : capitalize(repeatType?.id?.toString() ?? '')) as RecurrenceTypes,
    blockedTimeSlotId: objectId,
    startDateTime: !isNil(start) ? formatISODateTime(start) : undefined,
    durationInMinutes,
    userId,
    reasonForBlock,
    ...(notEmptyString(interval) ? { interval: Number(interval) } : {}),
    ...(!isNil(count) ? { count: Number(count) } : {}),
    ...(!isNil(repeatsOn) ? { repeatsOn: map(repeatsOn, 'id') } : {}),
    ...(!isNil(endsOn) ? { endsOn: formatDateTimeOffset(endsOn) } : {}),
  };
}

export function serializeProviderAvailability({
  objectId,
  start,
  userId,
  durationInMinutes,
  serviceFacility,
  recurrence,
}: Partial<CalendarEvent> & AppointmentForm): ProviderAvailabilityDTO {
  const {
    repeatType,
    interval,
    repeatsOn,
    endsOn,
    count,
  } = recurrence ?? {};
  return {
    recurrenceType: (isNil(repeatType)
      ? RecurrenceTypes.Single
      : capitalize(repeatType?.id?.toString() ?? '')) as RecurrenceTypes,
    userId,
    durationInMinutes,
    startDateTime: start ? formatISODateTime(start) : undefined,
    serviceFacilityId: serviceFacility?.serviceFacilityId,
    providerAvailabilityId: objectId,
    ...(notEmptyString(interval) ? { interval: Number(interval) } : {}),
    ...(!isNil(count) ? { count: Number(count) } : {}),
    ...(!isNil(repeatsOn) ? { repeatsOn: map(repeatsOn, 'id') } : {}),
    ...(!isNil(endsOn) ? { endsOn: formatISODateTime(endsOn) } : {}),
  };
}

export const validateDateIfFuture = (date: unknown) => {
  const dayValue = dayjs(date as never);
  return !dayValue.isAfter(formatDate(dayjs()), 'date') && dayValue.isValid();
};
export const validateDate = (date: unknown) => !isNil(date) && dayjs(date as never)?.isValid();

export const compactPatient = ({
  patientId,
  dateOfBirth,
  firstName,
  lastName,
  middleName,
  genderDescription,
  address,
  phoneNumber,
  email,
  formattedNames,
}: PatientDTO): PatientCompact => ({
  patientId,
  dateOfBirth: dateOfBirth ?? '',
  formattedDateOfBirth: formatDate(dateOfBirth ?? ''),
  formattedNames,
  firstName: firstName ?? '',
  lastName: lastName ?? '',
  middleName: middleName ?? '',
  genderDescription: genderDescription ?? '--',
  address,
  email: email ?? '',
  phoneNumber: phoneNumber?.phoneNumberWithAreaCode ?? '--',
});

const formatSelfIdentify = (value: string, isSelf: boolean) => (isSelf ? `Me (${value ?? ''})` : value ?? '');

export function getDisplayName(
  formattedNames?: FormattedNamesDTO | null,
  fallback = '',
): string {
  const displayName = formattedNames?.[displayNameKey] ?? fallback;
  return notEmptyString(displayName) ? displayName : fallback;
}

export function getInitials(
  formattedNames?: FormattedNamesDTO | null,
): string | null | undefined {
  return formattedNames?.[initialsKey];
}

export const normalizeUser = (user: UserDTO, userId?: number) => ({
  ...user,
  formattedNames: {
    ...user.formattedNames,
    fullNameWithCredentials: formatSelfIdentify(user.formattedNames?.fullNameWithCredentials ?? '', userId === user.userId),
    indexByLastNameWithCredentials: formatSelfIdentify(user.formattedNames?.indexByLastNameWithCredentials ?? '', userId === user.userId),
  },
});

export const normalizeUsers = (users: UserDTO[], userId?: number, sortBySelf = true): UserDTO[] => {
  const normalizedUsers = users.map((user: UserDTO) => normalizeUser(user, userId));
  return sortBySelf
    ? sortBy(
      normalizedUsers,
      (user) => user.userId !== userId,
    )
    : normalizedUsers;
};

export const getProfileImages = (
  originatorType: MediaOriginatorType = MediaOriginatorType.Patient,
  ids: (number | undefined)[] = [],
  pageProperties: PagePropertiesRequestDTO = {},
  generateUrls = true,
): Promise<MultiMediaResponse[]> => {
  const compacted = uniq(compact(ids));
  const multimediaType = originatorType === MediaOriginatorType.User
    ? MultimediaType.UserProfilePicture
    : MultimediaType.ProfilePicture;

  return compacted.length === 0
    ? Promise.resolve([])
    : getMultiMedia({
      generateUrls,
      ...(isNil(pageProperties) || isEmpty(pageProperties)
        ? {}
        : { pageProperties }
      ),
      list: compacted
        .map((id) => ({
          originatorId: id,
          originatorType,
          multimediaType,
        })),
    });
};

export function downloadFile({ fileName, contentType, contents }: FileResultDTO) {
  if (!isNil(contents)) {
    const a = document.createElement('a');
    a.href = compact(['data:', contentType, ';base64,', contents]).join('');
    a.download = fileName ?? '';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}

export async function downloadFileFromUrl(url: string, fileName: string): Promise<void> {
  const fileResponse = await fetch(url);
  const blob = await fileResponse.blob();

  // Create a blob URL and trigger download
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the blob URL
  window.URL.revokeObjectURL(blobUrl);
}
